import * as Colyseus from "colyseus.js";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { v4 as uuidv4 } from 'uuid';

import * as fu from "../../../common/FabulaUltimaGame";
import { FabulaUltimaGamePanel } from "../components/FabulaUltimaGamePanel";
import { FabulaUltimaJoinGamePanel, type GameRoom } from "../components/FabulaUltimaJoinGamePanel";
import { HelpButton } from "../components/HelpButton";

// Assets
import './FabulaUltimaApp.css';
import './FabulaUltimaTheme.css';
import { Alert } from "react-bootstrap";
import { fuText, type FuLocale } from "../i18n/fuLocale";

type ServerGameState = {
  s: fu.RoomState | null,
};

const PT_SANS_NARROW_REGULAR_URL = 'https://cdn.jsdelivr.net/gh/google/fonts@7c36d46208de28d9dc5ef3bf8c33716ea89c3ca9/ofl/ptsansnarrow/PT_Sans-Narrow-Web-Regular.ttf';

const client = new Colyseus.Client(process.env.REACT_APP_FABULA_ULTIMA_GAME_BACKEND_URL);
console.log(client);

type PendingRequest<ResponseType> = {
  resolve: (value: ResponseType) => void;
  reject: (reason?: any) => void;
  timeoutId: NodeJS.Timeout;
};

class RoomWrapper {
  pendingRequests: Map<string, PendingRequest<fu.Api.DetectIntentResponse>>;

  constructor(private room: Colyseus.Room) {
    this.pendingRequests = new Map<string, PendingRequest<fu.Api.DetectIntentResponse>>();
  }

  sendRunCommands(request: fu.Api.RunCommandsRequest): void {
    this.room.send(fu.Api.kRunCommandsName, request);
  }

  sendMoveStateTimeline(request: fu.Api.MoveStateTimelineRequest): void {
    this.room.send(fu.Api.kMoveStateTimelineName, request);
  }

  sendRollbackStateTimeline(request: fu.Api.RollbackStateTimelineRequest): void {
    this.room.send(fu.Api.kRollbackStateTimelineName, request);
  }

  async sendDetectIntent(request: fu.Api.DetectIntentRequest): Promise<fu.Api.DetectIntentResponse> {
    try {
      const response = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          this.pendingRequests.delete(request.requestId);
          reject(new Error("请求超时"));
        }, 10000);

        this.pendingRequests.set(request.requestId, {
          resolve,
          reject,
          timeoutId
        });

        this.room.send(fu.Api.kDetectIntentName, request);
      }) as fu.Api.DetectIntentResponse;

      console.log("收到响应：", response);
      return response;
    } catch (error) {
      console.log("请求失败：", error);
      throw error;
    }
  }

  sendHeartbeat() {
    this.room.send(fu.Api.kHeartbeatName, {});
  }

  sendUploadGameState(request: fu.Api.UploadGameStateRequest): void {
    this.room.send(fu.Api.kUploadGameStateName, request);
  }

  async leaveGame() {
    console.log("Leaving room");
    await this.room.leave();
  }
}

// Join a game in @roomId with @userName.
// Provided callbacks:
//    @setStateCallback: Called when a room state update is received.
//    @onBroadcastErrorCallback: Called when an error is broadcasted.
//    @onLeaveGameCallback: Called when the game is left.
async function joinGame(
  userName: string,
  roomId: string,
  isGm: boolean,
  setStateCallback: (s: ServerGameState) => void,
  onBroadcastErrorCallback: (error: string) => void,
  onLeaveGameCallback: () => void,
): Promise<RoomWrapper | null> {
  try {
    const joinOptions: fu.Api.RoomJoinOptions = {
      user: userName,
      isHost: isGm,
    };
    const room = await client.joinById(roomId, joinOptions);
    console.log(room.sessionId, "joined", room.name);

    room.onStateChange((state) => {
      console.log(room.name, "has new state:", state);
      setStateCallback({ s: state as fu.RoomState });
    });

    const roomWrapper = new RoomWrapper(room);

    room.onMessage(fu.Api.kDetectIntentResponseName, (message) => {
      console.log("收到DetectIntent响应：", message);
      const response = message as fu.Api.DetectIntentResponse;
      const request = roomWrapper.pendingRequests.get(response.requestId);
      if (request) {
        clearTimeout(request.timeoutId);
        roomWrapper.pendingRequests.delete(response.requestId);
        request.resolve(response);
      }
    })

    room.onMessage(fu.Api.kBroadcastErrorName, (message) => {
      console.log("收到BroadcastError消息：", message);
      const errorMessage = message as fu.Api.BroadcastErrorMessage;
      onBroadcastErrorCallback("服务器错误：" + errorMessage.error);
    })

    room.onError((code, message) => {
      console.log(room.sessionId, "couldn't join", room.name);
    });

    room.onLeave((code) => {
      console.log(room.sessionId, "left", room.name);
      onLeaveGameCallback();
    });

    return roomWrapper;
  } catch (e) {
    console.log("JOIN ERROR", e);
    return null;
  };
}

function FabulaUltimaApp() {
  const [locale, setLocale] = useState<FuLocale>(() => (localStorage.getItem("fabula-ultima-locale") as FuLocale) || "zh");
  const [allRooms, setAllRooms] = useState<Array<GameRoom>>([]);
  // The real-time game state from server
  const [serverGameState, setServerGameState] = useState<ServerGameState>({ s: null });
  const [isHost, setIsHost] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const roomRef = useRef<RoomWrapper | null>(null)

  // Initialize the room list only once.
  useEffect(
    () => {
      client.getAvailableRooms(fu.kRoomName).then(rooms => {
        setAllRooms(rooms.map((room) => {
          return {
            roomId: room.roomId,
            numClients: room.clients,
            maxClients: room.maxClients,
            displayName: room.metadata.displayName,
          };
        }));
      }).catch(e => {
        console.error(e);
      });
    }, [])

  const gameIsOngoing = (serverGameState.s?.ongoingGameState ?? null) !== null;
  const players = Array.from(serverGameState.s?.players.entries() ?? []).map(([sessionId, name]) => ({
    sessionId,
    name,
    isGm: sessionId === serverGameState.s?.host,
  }));

  const handleCloseAlert = () => {
    setErrorMessage(null);
  };

  const onBroadcastErrorCallback = (error: string) => {
    setErrorMessage(error);
  }

  const onLeaveGameCallback = () => {
    roomRef.current?.leaveGame();
    // Reset states
    roomRef.current = null;
    setServerGameState({ s: null });
    setErrorMessage(null);
  }

  const commandHistory =
    Array.from(
      serverGameState.s?.ongoingGameState?.commandHistory?.values() ?? []
    ).map(e => ({
      commandString: e.commandString,
      isSensitive: e.isSensitive,
      redactedCommandString: e.redactedCommandString,
      timestamp: e.timestamp,
    }));
  const stateTimeline =
    Array.from(serverGameState.s?.ongoingGameState?.stateTimeline?.values() ?? []).map(entry => ({
      commandString: entry.commandString,
      isSensitive: entry.isSensitive,
      redactedCommandString: entry.redactedCommandString,
      timestamp: entry.timestamp,
      initiatorName: entry.initiatorName,
    }));
  const stateTimelinePosition = serverGameState.s?.ongoingGameState?.stateTimelinePosition ?? 0;
  const buffHistory = Array.from(serverGameState.s?.buffHistory?.values() ?? []);

  return (
    <div className="App fu-app">
      <button className="fu-locale-toggle" type="button" onClick={() => setLocale(previous => { const next = previous === "zh" ? "en" : "zh"; localStorage.setItem("fabula-ultima-locale", next); return next; })}>{fuText(locale, 'localeToggle')}</button>
      <Helmet>
        <title>{fuText(locale, 'appTitle')}</title>
        <meta name="referrer" content="no-referrer" />
        <link
          rel="preload"
          href={PT_SANS_NARROW_REGULAR_URL}
          as="font"
          type="font/ttf"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      </Helmet>
      {errorMessage && (
        <Alert variant="danger" dismissible onClose={handleCloseAlert}>
          {errorMessage}
        </Alert>
      )}
      <FabulaUltimaJoinGamePanel
        gameIsOngoing={gameIsOngoing}
        rooms={allRooms}
        handleJoinGame={(user, roomId, isGm) => {
          joinGame(user, roomId, isGm, setServerGameState, onBroadcastErrorCallback, onLeaveGameCallback).then(room => {
            roomRef.current = room;
            setIsHost(isGm);
          })
        }}
        handleLeaveGame={() => {
          if (roomRef.current !== null) {
            roomRef.current.leaveGame();
            // Reset states
            roomRef.current = null;
            setServerGameState({ s: null });
          }
        }}
        players={players}
      />
      {gameIsOngoing && serverGameState.s && (
        <FabulaUltimaGamePanel
          characters={Array.from(serverGameState.s.ongoingGameState?.creatures?.values() ?? []).filter(c => c.kind === "ally").map(c => new fu.CreatureObject(c))}
          enemies={Array.from(serverGameState.s.ongoingGameState?.creatures?.values() ?? []).filter(c => c.kind === "enemy").map(c => new fu.CreatureObject(c))}
          onGenCommand={async (query) => {
            const request: fu.Api.DetectIntentRequest = {
              query: query,
              requestId: uuidv4(),
            };
            const response = await roomRef.current?.sendDetectIntent(request);
            return response!.response;
          }}
          onRunCommands={(commands) => {
            const request: fu.Api.RunCommandsRequest = {
              commands: commands,
            };
            roomRef.current?.sendRunCommands(request);
          }}
          onUploadGameState={(request) => {
            roomRef.current?.sendUploadGameState(request);
          }}
          onHeartbeat={() => {
            console.log("Sending heartbeat");
            roomRef.current?.sendHeartbeat();
          }}
          isHost={isHost}
          stateTimeline={stateTimeline}
          stateTimelinePosition={stateTimelinePosition}
          onMoveStateTimeline={(position) => {
            roomRef.current?.sendMoveStateTimeline({ position });
          }}
          onRollbackStateTimeline={(position) => { roomRef.current?.sendRollbackStateTimeline({ position }); }}
          commandHistory={commandHistory}
          buffHistory={buffHistory}
          locale={locale}
        />
      )}
      <HelpButton />
    </div>
  );
}

export default FabulaUltimaApp;
